import { FC, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Dropdown, Space, Button, Drawer, Grid } from 'antd';
import { LogoutOutlined, DoubleRightOutlined, DoubleLeftOutlined, UserOutlined, MenuOutlined, ProfileOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

import { logOut } from '../../redux/authentication/actionCreator';
import Menu from '../organisms/menu.component';
import FooterSection from '../organisms/footer.component';
import Notifications from '../organisms/notifications/notifications.component';
import ThemeSwitcher from '../molecules/theme-switcher.component';
import { APP_URLS } from '../../constants/global.constants';
import { usePermissions } from '../../hooks/usePermissions.hook';
import PlexusCanvas from '../atoms/plexus-canvas.component';

type Props = {
  logOut: Function,
  user?: any,
}

const AdminLayout: FC<Props> = ({ logOut, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccessTo } = usePermissions();
  const { Header, Footer, Sider, Content } = Layout;
  const screens = Grid.useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = !screens.lg;
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const isTecimac = user?.branchID?.id === '6831f88f5d8830001cb0d6c5';
  const brandLogo = isTecimac ? '/TecimacNew.png' : '/TecnocomNew-NF.png';
  const brandName = isTecimac ? 'Tecimac' : 'Tecnocom';

  const items: MenuProps['items'] = [
    {
      type: 'group',
      label: (
        <><span>{user.name}</span><br /><span>{user.email}</span></>
      ),
      children: [
        {
          type: 'divider',
        },
        {
          key: 'profile',
          label: (
            <a onClick={() => navigate(APP_URLS.PROFILE)}>
              <ProfileOutlined /> Mi Perfil
            </a>
          ),
        },
        {
          key: 'closeSesion',
          label: (
            <Button type="text" onClick={() => handleCloseSesion()}>
              <LogoutOutlined /> Cerrar sesión
            </Button>
          ),
        },
      ],
    },
  ];

  const handleCloseSesion = () => {
    logOut();
  };

  const onToggleCollapsed = () => {
    if (!isMobile) {
      setCollapsed(!collapsed);
    } else {
      setShowMobileMenu(!showMobileMenu);
    }
  };

  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname, isMobile]);

  return (
    <div className="admin-layout">
      <Layout style={{ height: '100vh', overflow: 'hidden' }}>
        <Header className="header">
              <>
                <PlexusCanvas side="left" className="header__shader" />
                <div className="header__menu">
                  <Button type="primary" onClick={onToggleCollapsed}>
                    {isMobile ? <MenuOutlined /> : collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
                  </Button>
                  <div className="header__tagline header__tagline--left">
                    <span>ESTRATEGAS DE INTEGRACIÓN</span>
                    <span>TECNOLÓGICA</span>
                  </div>                
                </div>
          <img className="header__brand" src={brandLogo} alt={brandName} />

          <div className="header__side--right">
            <div className="header__tagline header__tagline--right">
              <span>VISIONARIOS DE SOFTWARE</span>
              <span>INTELIGENTE</span>
            </div>

            <div className="header__user__nav">
              <ThemeSwitcher />
              {hasAccessTo('centro') && <Notifications user={user} />}
              <Dropdown menu={{ items }}>
                <button onClick={(e) => e.preventDefault()} className="header__user__avatar"><UserOutlined /></button>
              </Dropdown>
            </div>
          </div>
              </>
            </Header>
            <Layout hasSider style={{ height: 'calc(100vh - 64px)' }}>
              {!isMobile && (
                <Sider className="sidebar" collapsed={collapsed} width={240}>
                  <Menu inlineCollapsed={collapsed} theme="dark" />
                </Sider>
              )}
              <Layout style={{ overflowY: 'auto', background: 'var(--bg-main)' }}>
                <Content className="content">
                  <div className="content__page">
                    <Outlet />
                  </div>
                  <FooterSection isTecimac={isTecimac} />
                </Content>
              </Layout>
            </Layout>
          </Layout>
      <Drawer
        className="mobile-drawer"
        title={<img className="header__logo" src={brandLogo} alt={brandName} />}
        placement="left"
        onClose={() => setShowMobileMenu(false)}
        open={showMobileMenu}
        closeIcon={<ArrowLeftOutlined style={{ color: 'white' }} />}
        destroyOnClose
        styles={{
          body: { padding: 0, background: 'linear-gradient(180deg, #1e3a8a 0%, #0f172a 100%)' },
          header: { background: '#1e3a8a', borderBottom: '1px solid rgba(255,255,255,0.1)' }
        }}
      >
        <Menu inlineCollapsed={false} theme="dark" />
      </Drawer>
      <style>{`
      /* Header Styling */
      .ant-layout-header.header {
        background: #2e3270 !important; /* navy del banner TecnocomNew, sin costura */
        padding: 0 15px !important;
        height: 64px !important;
        line-height: 64px !important;
        display: flex !important;
        align-items: center;
        justify-content: space-between;
        position: relative;
        overflow: hidden;
        z-index: 1000;
        border-bottom: none !important;
      }

      /* Plexus animado (WebGL) en el lado izquierdo — mismo shader que el footer */
      .header__shader {
        position: absolute;
        top: 0;
        left: 0;
        width: 650px;
        height: 100%;
        pointer-events: none;
        z-index: 1;
      }

      .header__menu {
        display: flex;
        align-items: center;
        gap: 22px;
        flex: 1;
        z-index: 2;
      }

      .header__side--right {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex: 1;
        z-index: 2;
      }

      .header__brand {
        height: 46px;
        z-index: 2;
      }
      .header__tagline {
        display: flex;
        flex-direction: column;
        font-size: 12px;
        font-weight: 700;
        line-height: 1.35;
        letter-spacing: 0.5px;
        z-index: 2;
      }

      .header__tagline--left {
        color: #ffffff;
      }

      .header__tagline--right {
        color: #ffffff;
        text-align: right;
        margin-right: 28px;
      }
      /* .header__logo: NO borrar — el Drawer móvil sigue usándolo */
      .header__user__nav {
        display: flex;
        align-items: center;
        gap: 12px;
        z-index: 2;
        padding-right: 15px;
      }

      .header__user__nav > div, 
      .header__user__nav > span, 
      .header__user__nav > button,
      .header__user__avatar,
      .header__user__nav .ant-btn {
        background: rgba(255, 255, 255, 0.1) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
        color: #ffffff !important;
        padding: 8px !important;
        border-radius: 12px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        backdrop-filter: blur(10px) !important;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: none !important;
      }

      .header__user__avatar {
        width: 40px;
        height: 40px;
        font-size: 20px;
      }

      .header__user__nav *:hover {
        background: rgba(255, 255, 255, 0.25) !important;
        transform: translateY(-2px);
        border-color: rgba(255, 255, 255, 0.5) !important;
      }

      .header__user__nav .anticon {
        color: #ffffff !important;
        font-size: 18px !important;
      }

        @media (max-width: 768px) {
          .ant-layout-header.header {
            padding: 0 10px !important;
          }
          .header__tagline {
            display: none; /* sin taglines en móvil para no amontonar */
          }
          .header__menu {
            gap: 0;
          }
          .header__shader {
            width: 72vw; /* plexus también en móvil: mismo lado izquierdo, ancho proporcional */
          }
          .header__brand {
            height: 34px !important;
          }
          .header__user__nav {
            gap: 6px !important;
            padding-right: 5px !important;
          }
          .header__user__avatar {
            width: 32px !important;
            height: 32px !important;
            font-size: 16px !important;
          }
          .header__user__nav > div,
          .header__user__nav > span,
          .header__user__nav > button,
          .header__user__nav .ant-btn {
            padding: 6px !important;
            border-radius: 8px !important;
          }
        }

        /* Guard móvil: por debajo de lg (992px) el Sider NO debe verse jamás —
           ni en el render intermedio mientras useBreakpoint() actualiza su
           estado tras un resize (eso pintaba una franja delgada con iconos
           cortados que empujaba el contenido). El menú en móvil es el Drawer. */
        @media (max-width: 991px) {
          .ant-layout-sider.sidebar {
            display: none !important;
          }
        }

        /* Sidebar Styling - Solid Fixed */
        .ant-layout-sider.sidebar {
          background: linear-gradient(180deg, #1e3a8a 0%, #0f172a 100%) !important;
          border-right: 1px solid rgba(255, 255, 255, 0.05) !important;
          box-shadow: 10px 0 30px rgba(0,0,0,0.1) !important;
          height: calc(100vh - 64px) !important;
          overflow-y: auto !important;
          position: sticky !important;
          top: 64px;
          z-index: 999;
        }

        /* Mobile Drawer Dark Mode */
        .mobile-drawer .ant-drawer-content {
          background: linear-gradient(180deg, #1e3a8a 0%, #0f172a 100%) !important;
        }

        /* El drawer nunca debe desbordar el viewport: antd usa 378px fijos y en
           pantallas angostas (o con zoom) se corta por los bordes. */
        .mobile-drawer .ant-drawer-content-wrapper {
          width: min(85vw, 378px) !important;
        }

        /* El logo del título no tiene CSS propio (header__logo no está definido
           en ningún scss) → se pintaba a tamaño natural de imagen y desbordaba. */
        .mobile-drawer .ant-drawer-title img.header__logo {
          max-width: 100%;
          height: auto;
        }

        /* Aire horizontal del menú dentro del drawer */
        .mobile-drawer .ant-menu-root {
          padding-left: 6px;
          padding-right: 6px;
        }

        .mobile-drawer .ant-drawer-header {
          background: #1e3a8a !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
        }

        .mobile-drawer .ant-drawer-close {
          color: white !important;
        }

        .mobile-drawer .ant-drawer-title .header__logo {
          margin-left: 0 !important;
        }

        /* Ensure submenus dont cut off or overlap wrongly */
        .ant-menu-inline, .ant-menu-vertical {
          border-right: none !important;
        }

        /* Fix the cut off at bottom with a minimal margin */
        .sidebar .ant-menu-item:last-child {
          margin-bottom: 10px !important; 
        }

        /* Scrollbar del sidebar y drawer móvil OCULTA (el scroll sigue
           funcionando con rueda/gestos). scrollbar-width cubre Firefox y
           Chrome moderno; ::-webkit-scrollbar cubre el resto. */
        .sidebar, .mobile-drawer .ant-drawer-body {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .sidebar::-webkit-scrollbar, .mobile-drawer .ant-drawer-body::-webkit-scrollbar {
          width: 0;
          display: none;
        }

        /* Menu Dark Mode */
        .ant-layout-sider.sidebar .ant-menu-root, .mobile-drawer .ant-menu-root {
          background: transparent !important;
          border: none !important;
          padding-top: 20px;
        }

        /* Fixed: Submenu Popups background */
        .ant-menu-submenu-popup, .ant-menu-sub {
          background: #0f172a !important; 
          border-radius: 10px !important;
          padding: 4px !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.4) !important;
          z-index: 1010 !important;
        }

        .ant-menu-sub .ant-menu-item {
          margin: 4px 8px !important;
          width: calc(100% - 16px) !important;
        }

        /* Sub-menu Selection Highlight (Fix for Image 3) */
        .ant-menu-sub .ant-menu-item-selected {
           background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%) !important;
           color: white !important;
           box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
        }

        .ant-menu-sub .ant-menu-item-selected .ant-menu-title-content {
           color: white !important;
           font-weight: 700 !important;
        }

        .ant-menu-sub .ant-menu-item-selected .anticon {
           color: white !important;
        }

        .ant-menu-item, .ant-menu-submenu-title {
          height: 44px !important;
          line-height: 44px !important;
          margin: 6px 14px !important;
          width: auto !important;
          border-radius: 10px !important;
          color: rgba(255, 255, 255, 0.75) !important;
          transition: all 0.3s !important;
          font-weight: 500 !important;
        }

        .ant-menu-item .anticon, .ant-menu-submenu-title .anticon {
          font-size: 18px !important;
          color: rgba(255, 255, 255, 0.6) !important;
          transition: all 0.3s;
        }

        .ant-menu-item-selected {
          background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%) !important;
          color: white !important;
          box-shadow: 0 8px 16px rgba(59, 130, 246, 0.4) !important;
        }

        .ant-menu-item-selected .anticon {
          color: white !important;
          transform: scale(1.1);
        }

        .ant-menu-item:hover:not(.ant-menu-item-selected), 
        .ant-menu-submenu-title:hover {
          background: rgba(255, 255, 255, 0.08) !important;
          color: white !important;
        }

        .ant-menu-item:hover .anticon {
          color: white !important;
        }

        .ant-menu-submenu-arrow {
          color: rgba(255, 255, 255, 0.5) !important;
        }

        /* Animations */
        @keyframes liquidMove {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(180deg) scale(1.3); }
          100% { transform: rotate(360deg) scale(1); }
        }

        .admin-layout .content {
          padding: 24px 30px !important;
          background: var(--bg-main) !important; /* Clean dynamic background */
          min-height: calc(100vh - 64px) !important;
          display: flex !important;
          flex-direction: column !important;
        }

        /* Sin esto, en páginas largas los hijos de .content (página + footer)
           se comprimen (flex-shrink por defecto) porque antd limita la altura
           del Content → el footer salía cortado en una franja. */
        .admin-layout .content > * {
          flex-shrink: 0 !important;
        }

        /* Sticky footer definitivo: .content es columna flex con altura mínima
           de viewport; .content__page CRECE (flex:1) y empuja el footer al fondo
           cuando la página es corta. La separación con el contenido la da el
           margin-top: 32px que el footer ya trae en scss/layout/footer.scss. */
        .admin-layout .content__page {
          flex: 1 0 auto;
        }

        @media (max-width: 768px) {
          .admin-layout .content {
            padding: 15px 12px !important;
          }
        }

        /* Fix white gaps and bottom issue */
        .admin-layout .ant-layout {
           background: var(--bg-main) !important; /* Unified dynamic background for content area */
        }

        .admin-layout {
          background: var(--bg-main) !important;
        }

        .ant-layout-sider.sidebar {
          background: #1e3a8a !important; /* Keep Sidebar Dark */
        }

        /* Tighter Sub-menu Spacing */
        .ant-menu-sub .ant-menu-item {
          margin: 2px 14px !important; /* Much tighter for sub-items */
          height: 38px !important;
          line-height: 38px !important;
        }

        .ant-menu-item-selected:hover {
          background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%) !important;
          opacity: 0.9;
        }

        .ant-menu-item-selected:hover *,
        .ant-menu-item-selected:hover .anticon,
        .ant-menu-item-selected:hover span,
        .ant-menu-item-selected:hover a {
          color: white !important;
        }

        /* Staggered menu reveal — fires on mount (desktop load + mobile drawer open
           via destroyOnClose). Each item fades + slides in with a small cascade. */
        @keyframes menuStaggerIn {
          from { opacity: 0; transform: translateX(-12px); }
          to   { opacity: 1; transform: translateX(0); }
        }

        .ant-menu-root > .ant-menu-item,
        .ant-menu-root > .ant-menu-submenu {
          animation: menuStaggerIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .ant-menu-root > .ant-menu-item:nth-child(1),
        .ant-menu-root > .ant-menu-submenu:nth-child(1) { animation-delay: 0.04s; }
        .ant-menu-root > .ant-menu-item:nth-child(2),
        .ant-menu-root > .ant-menu-submenu:nth-child(2) { animation-delay: 0.08s; }
        .ant-menu-root > .ant-menu-item:nth-child(3),
        .ant-menu-root > .ant-menu-submenu:nth-child(3) { animation-delay: 0.12s; }
        .ant-menu-root > .ant-menu-item:nth-child(4),
        .ant-menu-root > .ant-menu-submenu:nth-child(4) { animation-delay: 0.16s; }
        .ant-menu-root > .ant-menu-item:nth-child(5),
        .ant-menu-root > .ant-menu-submenu:nth-child(5) { animation-delay: 0.20s; }
        .ant-menu-root > .ant-menu-item:nth-child(6),
        .ant-menu-root > .ant-menu-submenu:nth-child(6) { animation-delay: 0.24s; }
        .ant-menu-root > .ant-menu-item:nth-child(7),
        .ant-menu-root > .ant-menu-submenu:nth-child(7) { animation-delay: 0.28s; }
        .ant-menu-root > .ant-menu-item:nth-child(8),
        .ant-menu-root > .ant-menu-submenu:nth-child(8) { animation-delay: 0.32s; }
        .ant-menu-root > .ant-menu-item:nth-child(9),
        .ant-menu-root > .ant-menu-submenu:nth-child(9) { animation-delay: 0.36s; }
        .ant-menu-root > .ant-menu-item:nth-child(10),
        .ant-menu-root > .ant-menu-submenu:nth-child(10) { animation-delay: 0.40s; }
        .ant-menu-root > .ant-menu-item:nth-child(11),
        .ant-menu-root > .ant-menu-submenu:nth-child(11) { animation-delay: 0.44s; }
        .ant-menu-root > .ant-menu-item:nth-child(12),
        .ant-menu-root > .ant-menu-submenu:nth-child(12) { animation-delay: 0.48s; }
        .ant-menu-root > .ant-menu-item:nth-child(n+13),
        .ant-menu-root > .ant-menu-submenu:nth-child(n+13) { animation-delay: 0.52s; }

        /* Respect reduced-motion preference */
        @media (prefers-reduced-motion: reduce) {
          .ant-menu-root > .ant-menu-item,
          .ant-menu-root > .ant-menu-submenu {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.users?.data || null,
});

export default connect(mapStateToProps, { logOut })(AdminLayout);